import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Save, Loader2 } from 'lucide-react';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { NavigationBar } from '../components/ui/NavigationBar';
import { AlertModal } from '../components/ui/AlertModal';
import { useTranslations } from '../i18n';
import { useQueryClient } from '@tanstack/react-query';
import { REMINDER_KEYS } from '../hooks/useCarData';
import { ReminderFormSection } from '../components/cars/ReminderFormSection';
import type { ReminderFormData } from '../types/reminders';

interface ReminderFormProps {
  carId: string;
  reminderId?: string;
  onClose: () => void;
  isReadOnly?: boolean;
}

export function ReminderForm({ carId, reminderId, onClose, isReadOnly = false }: ReminderFormProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const outletContext = useOutletContext<any>() || {};
  const { t } = useTranslations();
  const effectiveUserId = outletContext.effectiveUserId || user?.id;

  const [reminderData, setReminderData] = useState<ReminderFormData | null>(null);
  const [initialReminderData, setInitialReminderData] = useState<string>('null');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });

  useEffect(() => {
    loadData();
  }, [reminderId]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (reminderId) {
        const { data: remData } = await supabase.from('reminders').select('*').eq('id', reminderId).single();
        if (remData) {
          const formatted: ReminderFormData = {
            name: remData.name || '',
            description: remData.description || '',
            remind_date: remData.remind_date,
            remind_time: remData.remind_time,
            extra_reminders: remData.extra_reminders,
            repeat_yearly: remData.repeat_yearly,
            is_active: remData.is_active,
            action_required: remData.action_required
          };
          setReminderData(formatted);
          setInitialReminderData(JSON.stringify(formatted));
        }
      } else {
        // New reminder defaults
        const nextWeek = new Date();
        nextWeek.setDate(nextWeek.getDate() + 7);
        const defaultValue: ReminderFormData = {
          name: '',
          description: '',
          remind_date: nextWeek.toISOString().split('T')[0],
          remind_time: '12:00',
          extra_reminders: false,
          repeat_yearly: false,
          is_active: true,
          action_required: false
        };
        setReminderData(defaultValue);
        setInitialReminderData(JSON.stringify(defaultValue));
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = JSON.stringify(reminderData) !== initialReminderData;
  const isValid = !!reminderData?.name?.trim();

  const handleSubmit = async (): Promise<boolean> => {
    if (!reminderData || saving || isReadOnly || !isValid) return false;

    if (reminderData && reminderData.is_active) {
      const now = new Date();
      const remindDateTime = new Date(`${reminderData.remind_date}T${reminderData.remind_time || '00:00'}`);
      // Must be at least 1 minute in the future
      if (remindDateTime.getTime() <= now.getTime() + 60000) {
        setAlertConfig({ 
          isOpen: true, 
          title: t.common.error, 
          message: t.reminders.futureTimeError 
        });
        return false;
      }
    }

    setSaving(true);
    try {
      let finalData = { ...reminderData };
      if (finalData.remind_time) {
        const [h, m] = finalData.remind_time.split(':').map(Number);
        const roundedM = Math.round(m / 5) * 5;
        const finalM = roundedM === 60 ? 55 : roundedM;
        finalData.remind_time = `${h.toString().padStart(2, '0')}:${finalM.toString().padStart(2, '0')}`;
      }

      if (reminderId) {
        await supabase.from('reminders').update({
          ...finalData,
          is_seen: false,
          action_required: false,
          last_remind_sequence: 0,
          updated_at: new Date().toISOString()
        }).eq('id', reminderId);
      } else {
        await supabase.from('reminders').insert({
          ...finalData,
          car_id: carId,
          user_id: effectiveUserId
        });
      }

      await queryClient.invalidateQueries({ queryKey: REMINDER_KEYS.list(carId) });
      onClose();
      return true;
    } catch (err: any) {
      console.error('Error saving:', err);
      let errorMessage = t.common.errorSaving;
      if (err.code === '23514') {
        errorMessage = t.common.mandatoryFieldsError;
      }
      setAlertConfig({ isOpen: true, title: t.common.error, message: errorMessage });
      return false;
    } finally {
      setSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!reminderId || !user) return;
    try {
      const { error } = await supabase.from('reminders').delete().eq('id', reminderId);
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: REMINDER_KEYS.list(carId) });
      onClose();
    } catch (err) {
      console.error('Error deleting reminder:', err);
      setAlertConfig({ isOpen: true, title: t.common.error, message: (t.common as any).errorDeleting || 'Neizdevās izdzēst atgādinājumu' });
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleSimulate = async () => {
    if (!reminderId || saving) return;
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL || 'https://mycarcosts.gtreulands.workers.dev'}/reminders/simulate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ reminder_id: reminderId })
      });

      if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText);
      }

      setAlertConfig({
        isOpen: true,
        title: t.common.ok,
        message: t.reminders.simulateSuccess
      });
    } catch (err: any) {
      console.error('Error simulating reminder:', err);
      setAlertConfig({
        isOpen: true,
        title: t.common.error,
        message: err.message || t.common.error
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-[100] bg-gradient-theme">
        <Loader2 className="animate-spin text-blue-600 dark:text-blue-400" size={32} />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-gradient-theme flex flex-col font-primary overflow-hidden">
      <NavigationBar
        title={reminderId ? t.reminders.editReminder : t.reminders.addReminder}
        onBack={() => {
          if (hasChanges && !isReadOnly) {
            setShowConfirmModal(true);
          } else {
            onClose();
          }
        }}
        fixed={false}
        rightAction={(!isReadOnly && hasChanges && isValid) ? {
          icon: Save,
          onClick: handleSubmit,
          disabled: saving,
          className: saving ? 'opacity-50' : ''
        } : undefined}
      />

      <div className="flex-1 overflow-y-auto no-scrollbar">
        <div className="max-w-2xl mx-auto p-4 pb-32">
            <ReminderFormSection
              data={reminderData}
              onChange={setReminderData}
              isReadOnly={isReadOnly}
              showNameField={true}
              isStandalone={true}
              onDelete={reminderId ? () => setShowDeleteModal(true) : undefined}
              isAdminView={outletContext.isAdminView}
              onSimulate={handleSimulate}
              reminderId={reminderId}
              onMarkSeen={async () => {
                if (!reminderId || !reminderData) return;
                setSaving(true);
                try {
                  const updateData: any = { 
                    is_seen: true, 
                    action_required: false,
                    last_remind_sequence: 0, // Reset sequence when marked seen
                    updated_at: new Date().toISOString()
                  };

                  // If it's a yearly repeating reminder, move it to next year
                  if (reminderData.repeat_yearly && reminderData.remind_date) {
                    const currentDate = new Date(reminderData.remind_date);
                    currentDate.setFullYear(currentDate.getFullYear() + 1);
                    updateData.remind_date = currentDate.toISOString().split('T')[0];
                    updateData.is_seen = false; // Reset seen for next year
                  }

                  await supabase.from('reminders').update(updateData).eq('id', reminderId);
                  await queryClient.invalidateQueries({ queryKey: REMINDER_KEYS.list(carId) });
                  onClose();
                } catch (err) {
                  console.error('Error marking seen:', err);
                } finally {
                  setSaving(false);
                }
              }}
            />
        </div>
      </div>

      <AlertModal
        isOpen={alertConfig.isOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertConfig({ ...alertConfig, isOpen: false })}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        title={t.reminders.deleteReminder}
        message={(t.common as any).confirmDelete || 'Vai tiešām vēlaties dzēst šo ierakstu?'}
        confirmLabel={t.common.delete}
        cancelLabel={t.common.cancel}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        isDestructive={true}
      />

      <ConfirmModal
        isOpen={showConfirmModal}
        title={t.common.unsavedChangesTitle}
        message={t.common.unsavedChangesMessage}
        confirmLabel={t.common.save}
        cancelLabel={t.common.dontSave}
        onConfirm={async () => {
          const success = await handleSubmit();
          if (success) {
            setShowConfirmModal(false);
            onClose();
          }
        }}
        onCancel={() => {
          setShowConfirmModal(false);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        isDestructive={false}
      />
    </div>
  );
}
